/**
 * Registro do que cada campo de arquivo aceita.
 *
 * O painel tinha **seis grafias de `accept` para a mesma intenção** — o anexo de imagem era
 * `image/jpeg,image/jpg,image/png,image/gif,image/webp` numa tela e a mesma lista em outra ordem
 * na tela irmã, o comprovante era `image/jpeg,image/png,image/jpg,application/pdf` em três lugares
 * e `.jpg,.jpeg,.png` num quarto, e havia dois `FILE_ACCEPT` locais com conteúdos diferentes. Cada
 * cópia foi escrita olhando para uma regra do backend diferente, e algumas divergiam dela:
 * o anexo de suporte aceitava `image/*` no navegador e `mimes:jpg,jpeg,png,webp,pdf` no servidor,
 * então um GIF passava pelo seletor e voltava como erro de validação.
 *
 * Aqui cada token é uma **intenção** ("anexo", "comprovante", "currículo"), e a lista de extensões
 * espelha a regra `mimes:` do endpoint correspondente. Trocar o que um campo aceita é editar este
 * arquivo, e o rótulo em pt-BR usado na dica e na mensagem de recusa vem junto — eram frases
 * soltas em cada página, com listas que já não batiam com o `accept` ao lado.
 */
export type TFileAccept =
  | 'image'
  | 'coverImage'
  | 'photo'
  | 'attachment'
  | 'receipt'
  | 'document'
  | 'media'
  | 'workFile'
  | 'video'
  | 'transcript'
  | 'font'
  | 'certificateTemplate'
  | 'admissionDocument'

export interface IFileTypeConfig {
  /** Extensões aceitas, em minúsculas e sem ponto. É o que a validação usa. */
  extensions: string[]
  /** Lista em pt-BR para a dica do campo e para a mensagem de recusa. */
  label: string
}

export const FILE_TYPES: Record<TFileAccept, IFileTypeConfig> = {
  /**
   * Imagem de conteúdo: banner, thumb de curso e evento, foto de professor, depoimento.
   * Backend: `mimes:jpeg,jpg,png,gif,webp`.
   *
   * `image`, `coverImage` e `photo` são a mesma ideia com três listas, porque **três endpoints
   * discordam sobre a mesma figura** — a capa do blog não aceita GIF e a foto de usuário não aceita
   * nem GIF nem WebP. Estão aqui separados em vez de unificados de propósito: alargar o seletor
   * para além do que o servidor aceita é o defeito que este eixo foi fechar, e ter as três listas
   * num arquivo só deixa a divergência visível para quem for resolvê-la no backend.
   */
  image: { extensions: ['jpg', 'jpeg', 'png', 'gif', 'webp'], label: 'JPG, PNG, GIF ou WebP' },
  /** Capa de post do blog. Backend: `mimes:webp,png,jpg,jpeg` — sem GIF. */
  coverImage: { extensions: ['jpg', 'jpeg', 'png', 'webp'], label: 'JPG, PNG ou WebP' },
  /** Foto de perfil de usuário. Backend: `mimes:jpeg,png,jpg`. */
  photo: { extensions: ['jpg', 'jpeg', 'png'], label: 'JPG ou PNG' },
  /** Anexo de chamado e documento de aproveitamento. Backend: `mimes:jpg,jpeg,png,webp,pdf`. */
  attachment: { extensions: ['jpg', 'jpeg', 'png', 'webp', 'pdf'], label: 'PDF ou imagem (JPG, PNG, WebP)' },
  /**
   * Comprovante de pagamento. Backend: `mimes:jpeg,jpg,png,pdf` — **sem WebP**, e é por isso que
   * não é o mesmo token do anexo: um comprovante `.webp` passaria no seletor e seria recusado no
   * envio.
   */
  receipt: { extensions: ['jpg', 'jpeg', 'png', 'pdf'], label: 'PDF ou imagem (JPG, PNG)' },
  /** Currículo do banco de talentos. Backend: `mimes:pdf,doc,docx,png,jpg,jpeg`. */
  document: { extensions: ['pdf', 'doc', 'docx', 'jpg', 'jpeg', 'png'], label: 'PDF, DOC, DOCX ou imagem (JPG, PNG)' },
  /** Mídia do AbraMídia: imagem ou vídeo curto. */
  media: { extensions: ['jpg', 'jpeg', 'png', 'gif', 'mp4'], label: 'JPG, PNG, GIF ou MP4' },
  /**
   * Arquivo de entrega de trabalho. Backend:
   * `mimes:pdf,jpg,jpeg,png,xls,xlsx,doc,docx,ppt,pptx,zip`.
   *
   * A cópia que existia na página listava só os formatos novos do Office (`xlsx`, `pptx`, `docx`)
   * e recusava no navegador o `.xls`/`.doc`/`.ppt` que o servidor aceita — um aluno com um trabalho
   * antigo não conseguia nem selecionar o arquivo.
   */
  workFile: {
    extensions: ['pdf', 'jpg', 'jpeg', 'png', 'xls', 'xlsx', 'doc', 'docx', 'ppt', 'pptx', 'zip'],
    label: 'PDF, JPG, PNG, XLS(X), DOC(X), PPT(X) ou ZIP'
  },
  /** Vídeo MP4 (evento, depoimento). Backend: `mimetypes:video/mp4|mimes:mp4`. */
  video: { extensions: ['mp4'], label: 'MP4' },
  /**
   * Transcrição de uma conversa, lida pela IA do Brilha Magenta. Backend: extensão `md`/`txt`
   * (`BrilhaMagentaAiSuggestionRequest`) — por extensão porque o MIME de um `.md` varia.
   */
  transcript: { extensions: ['md', 'txt'], label: 'MD ou TXT' },
  /**
   * Fonte dos Banners Home. Backend: extensão `woff2`/`woff`/`ttf`/`otf` (`WebsiteFontStoreRequest`)
   * — por extensão porque o MIME de uma fonte varia entre sistemas.
   */
  font: { extensions: ['woff2', 'woff', 'ttf', 'otf'], label: 'WOFF2, WOFF, TTF ou OTF' },
  /** Modelo em branco do certificado de evento. Backend: `mimes:pdf` (`EventCertificateTemplateRequest`). */
  certificateTemplate: { extensions: ['pdf'], label: 'PDF' },
  /** Documento do formulário de admissão. Backend: `mimes:pdf,jpg,jpeg,png` (`AdmissionDraftService`). */
  admissionDocument: { extensions: ['pdf', 'jpg', 'jpeg', 'png'], label: 'PDF, JPG ou PNG' }
}

/**
 * Valor do atributo `accept` do `<input type="file">`, derivado das extensões.
 *
 * É por extensão e não por MIME de propósito: o MIME de XLSX/PPTX/DOCX/ZIP varia entre
 * navegadores (e vem vazio num arquivo arrastado de certos gerenciadores), então filtrar por ele
 * escondia arquivos válidos do seletor. A validação usa a mesma lista, pelo mesmo motivo.
 */
export const acceptAttribute = (accept: TFileAccept): string =>
  FILE_TYPES[accept].extensions.map((extension) => `.${extension}`).join(',')

/** Extensão em minúsculas, sem ponto. `''` quando o nome não tem extensão. */
export const fileExtension = (fileName: string): string => fileName.split('.').pop()?.toLowerCase() ?? ''

const IMAGE_EXTENSIONS = ['jpg', 'jpeg', 'png', 'webp']

/** Se o arquivo é uma imagem que dá para recomprimir (GIF fica de fora: perde a animação). */
export const isCompressibleImage = (file: File): boolean => IMAGE_EXTENSIONS.includes(fileExtension(file.name))

/** Se o arquivo rende uma miniatura na hora, sem conversão. */
export const isPreviewableImage = (file: File): boolean =>
  [...IMAGE_EXTENSIONS, 'gif'].includes(fileExtension(file.name))
